import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CustomerAuthProvider, useCustomerAuth } from "./contexts/CustomerAuthContext";
import { CartProvider } from "./contexts/CartContext";
import { Toaster } from "./components/ui/sonner";
import { Layout } from "./components/Layout";
import { CustomerLayout } from "./components/CustomerLayout";

// Admin Pages
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Products } from "./pages/Products";
import { Customers } from "./pages/Customers";
import { Suppliers } from "./pages/Suppliers";
import { Sales } from "./pages/Sales";
import { Coupons } from "./pages/Coupons";
import { Orders } from "./pages/Orders";
import { OrderDetail } from "./pages/OrderDetail";
import { OrderDates } from "./pages/OrderDates";
import { PrintOrders } from "./pages/PrintOrders";
import { AdminUsers } from "./pages/AdminUsers";
import { ProxyOrder } from "./pages/ProxyOrder";
import { OrderConfirm } from "./pages/OrderConfirm";
import { Units } from "./pages/Units";
import { Profit } from "./pages/Profit";
import { Categories } from "./pages/Categories";
import { QuoteRequests } from "./pages/QuoteRequests";
import { PaymentPreparation } from "./pages/PaymentPreparation";
import { MasterManagement } from "./pages/MasterManagement";
import { CreditPayments } from "./pages/CreditPayments";
import { TewariBox } from "./pages/TewariBox";

// Customer Pages
import { CustomerLogin } from "./pages/customer/CustomerLogin";
import { CustomerCatalog } from "./pages/customer/CustomerCatalog";
import { CustomerCart } from "./pages/customer/CustomerCart";
import { CustomerOrders } from "./pages/customer/CustomerOrders";
import { CustomerOrderDetail } from "./pages/customer/CustomerOrderDetail";
import { QuoteRequest } from "./pages/customer/QuoteRequest";
import { CustomerHelp } from "./pages/customer/CustomerHelp";

const AdminRoute = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  if (!token) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const AdminPublicRoute = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  if (token) return <Navigate to="/dashboard" replace />;
  return children;
};

const CustomerRoute = ({ children }) => {
  const { token, loading } = useCustomerAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  if (!token) return <Navigate to="/customer/login" replace />;
  return <CustomerLayout>{children}</CustomerLayout>;
};

const CustomerPublicRoute = ({ children }) => {
  const { token, loading } = useCustomerAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  if (token) return <Navigate to="/customer/catalog" replace />;
  return children;
};

function AdminRoutes() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<AdminPublicRoute><Login /></AdminPublicRoute>} />
        <Route path="/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
        <Route path="/products" element={<AdminRoute><Products /></AdminRoute>} />
        <Route path="/customers" element={<AdminRoute><Customers /></AdminRoute>} />
        <Route path="/suppliers" element={<AdminRoute><Suppliers /></AdminRoute>} />
        <Route path="/sales" element={<AdminRoute><Sales /></AdminRoute>} />
        <Route path="/coupons" element={<AdminRoute><Coupons /></AdminRoute>} />
        <Route path="/orders" element={<AdminRoute><Orders /></AdminRoute>} />
        <Route path="/orders/:id" element={<AdminRoute><OrderDetail /></AdminRoute>} />
        <Route path="/order-dates" element={<AdminRoute><OrderDates /></AdminRoute>} />
        <Route path="/print" element={<AdminRoute><PrintOrders /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/proxy-order" element={<AdminRoute><ProxyOrder /></AdminRoute>} />
        <Route path="/order-confirm" element={<AdminRoute><OrderConfirm /></AdminRoute>} />
        <Route path="/units" element={<AdminRoute><MasterManagement /></AdminRoute>} />
        <Route path="/categories" element={<AdminRoute><MasterManagement /></AdminRoute>} />
        <Route path="/master" element={<AdminRoute><MasterManagement /></AdminRoute>} />
        <Route path="/profit" element={<AdminRoute><Profit /></AdminRoute>} />
        <Route path="/payment-prep" element={<AdminRoute><PaymentPreparation /></AdminRoute>} />
        <Route path="/credit-payments" element={<AdminRoute><CreditPayments /></AdminRoute>} />
        <Route path="/tewari-box" element={<AdminRoute><TewariBox /></AdminRoute>} />
        <Route path="/quote-requests" element={<AdminRoute><QuoteRequests /></AdminRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AuthProvider>
  );
}

function CustomerRoutes() {
  return (
    <CustomerAuthProvider>
      <CartProvider>
        <Routes>
          <Route path="/login" element={<CustomerPublicRoute><CustomerLogin /></CustomerPublicRoute>} />
          <Route path="/catalog" element={<CustomerRoute><CustomerCatalog /></CustomerRoute>} />
          <Route path="/cart" element={<CustomerRoute><CustomerCart /></CustomerRoute>} />
          <Route path="/orders" element={<CustomerRoute><CustomerOrders /></CustomerRoute>} />
          <Route path="/orders/:id" element={<CustomerRoute><CustomerOrderDetail /></CustomerRoute>} />
          <Route path="/help" element={<CustomerRoute><CustomerHelp /></CustomerRoute>} />
          <Route path="/" element={<Navigate to="/customer/catalog" replace />} />
          <Route path="*" element={<Navigate to="/customer/catalog" replace />} />
        </Routes>
      </CartProvider>
    </CustomerAuthProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/customer/*" element={<CustomerRoutes />} />
        <Route path="/*" element={<AdminRoutes />} />
      </Routes>
      <Toaster position="top-right" richColors duration={2500} />
    </BrowserRouter>
  );
}

export default App;
